import type { LightSourceParams, LightingParams } from '@/types';

/** fal lighting_style 的 18 个合法枚举（字面量联合类型，拼写错误在编译期拦截） */
export type LightingStyle =
  | 'natural'
  | 'studio'
  | 'golden_hour'
  | 'blue_hour'
  | 'dramatic'
  | 'soft'
  | 'hard'
  | 'backlight'
  | 'side_light'
  | 'front_light'
  | 'rim_light'
  | 'sunset'
  | 'sunrise'
  | 'neon'
  | 'candlelight'
  | 'moonlight'
  | 'spotlight'
  | 'ambient';

export type LightingPresetId =
  | 'custom'
  | 'three-point'
  | 'rembrandt'
  | 'split'
  | 'top-drama'
  | 'anime-soft'
  | 'cyberpunk'
  | 'natural'
  | 'golden-hour'
  | 'blue-hour'
  | 'high-key'
  | 'low-key'
  | 'rim'
  | 'silhouette'
  | 'neon'
  | 'scene-light'
  | 'chiaroscuro'
  | 'campfire'
  | 'moon-mystery';

export interface LightingPreset {
  id: Exclude<LightingPresetId, 'custom'>;
  label: string;
  /** 主灯初始角度（度）；三点布光为多灯种子，见 presetLights */
  horizontalAngle: number;
  verticalAngle: number;
  lightType: 'hard' | 'soft';
  intensity: number;
  color: string;
  /** 生成映射：fal lighting_style 枚举 */
  lightingStyle: LightingStyle;
  /** 多灯种子预设：一次创建多盏灯（生成映射取主灯） */
  presetLights?: Omit<LightSourceParams, 'id' | 'isMain'>[];
}

/** 灯光预设表（图二原版 18 个，映射 fal lighting_style，方案 §6） */
export const LIGHTING_PRESETS: LightingPreset[] = [
  { id: 'three-point', label: '三点布光', horizontalAngle: -45, verticalAngle: 45, lightType: 'soft', intensity: 7, color: '#FFFFFF', lightingStyle: 'studio',
    presetLights: [
      { label: '主光', horizontalAngle: -45, verticalAngle: 45, lightType: 'soft', intensity: 7, color: '#FFFFFF' },
      { label: '补光 1', horizontalAngle: 45, verticalAngle: 30, lightType: 'soft', intensity: 4, color: '#FFFFFF' },
      { label: '补光 2', horizontalAngle: 170, verticalAngle: 35, lightType: 'hard', intensity: 6, color: '#FFFFFF' },
    ] },
  { id: 'rembrandt', label: '伦勃朗布光', horizontalAngle: -60, verticalAngle: 45, lightType: 'soft', intensity: 6, color: '#FFF2E0', lightingStyle: 'side_light' },
  { id: 'split', label: '分割光', horizontalAngle: -90, verticalAngle: 30, lightType: 'hard', intensity: 6, color: '#FFFFFF', lightingStyle: 'side_light' },
  { id: 'top-drama', label: '顶光戏剧', horizontalAngle: 0, verticalAngle: 90, lightType: 'hard', intensity: 8, color: '#FFFFFF', lightingStyle: 'dramatic' },
  { id: 'anime-soft', label: '动漫柔光', horizontalAngle: 30, verticalAngle: 60, lightType: 'soft', intensity: 5, color: '#FFF8F0', lightingStyle: 'soft' },
  { id: 'cyberpunk', label: '赛博朋克', horizontalAngle: -120, verticalAngle: 30, lightType: 'hard', intensity: 9, color: '#00E5FF', lightingStyle: 'neon' },
  { id: 'natural', label: '自然光', horizontalAngle: -30, verticalAngle: 55, lightType: 'soft', intensity: 5, color: '#FFFDF5', lightingStyle: 'natural' },
  { id: 'golden-hour', label: '黄金时刻', horizontalAngle: -70, verticalAngle: 15, lightType: 'soft', intensity: 7, color: '#FFB35C', lightingStyle: 'golden_hour' },
  { id: 'blue-hour', label: '蓝调时刻', horizontalAngle: 60, verticalAngle: 20, lightType: 'soft', intensity: 4, color: '#5C7CFF', lightingStyle: 'blue_hour' },
  { id: 'high-key', label: '高调光', horizontalAngle: 0, verticalAngle: 70, lightType: 'soft', intensity: 9, color: '#FFFFFF', lightingStyle: 'ambient' },
  { id: 'low-key', label: '低调光', horizontalAngle: -50, verticalAngle: 40, lightType: 'hard', intensity: 3, color: '#FFE9C4', lightingStyle: 'spotlight' },
  { id: 'rim', label: '轮廓光', horizontalAngle: 150, verticalAngle: 30, lightType: 'hard', intensity: 8, color: '#FFFFFF', lightingStyle: 'rim_light' },
  { id: 'silhouette', label: '剪影', horizontalAngle: 180, verticalAngle: 25, lightType: 'hard', intensity: 9, color: '#FFFFFF', lightingStyle: 'backlight' },
  { id: 'neon', label: '霓虹灯', horizontalAngle: -110, verticalAngle: 20, lightType: 'hard', intensity: 8, color: '#FF3DCB', lightingStyle: 'neon' },
  { id: 'scene-light', label: '实景光', horizontalAngle: 0, verticalAngle: 60, lightType: 'soft', intensity: 5, color: '#FFF6E8', lightingStyle: 'natural' },
  { id: 'chiaroscuro', label: '明暗对比', horizontalAngle: -75, verticalAngle: 35, lightType: 'hard', intensity: 7, color: '#F5EDE0', lightingStyle: 'hard' },
  { id: 'campfire', label: '篝火光', horizontalAngle: -40, verticalAngle: 10, lightType: 'soft', intensity: 6, color: '#FF8A3C', lightingStyle: 'candlelight' },
  { id: 'moon-mystery', label: '月夜神秘', horizontalAngle: 45, verticalAngle: 65, lightType: 'soft', intensity: 3, color: '#B8CFFF', lightingStyle: 'moonlight' },
];

/** 灯光数量上限（主灯 + 2 补光） */
export const MAX_LIGHTS = 3;

/** 水平方位角限定到 [-180, 180] */
export const clampHorizontalAngle = (v: number): number => {
  if (!Number.isFinite(v)) return 0;
  let r = Math.round(v) % 360;
  if (r > 180) r -= 360;
  else if (r < -180) r += 360;
  return r;
};

/** 仰角限定到 [-20, 90]（负值 = 底光） */
export const clampVerticalAngle = (v: number): number =>
  Math.max(-20, Math.min(90, Math.round(Number.isFinite(v) ? v : 60)));

/** 强度限定到 0~10 */
export const clampIntensity = (v: number): number =>
  Math.max(0, Math.min(10, Math.round(Number.isFinite(v) ? v : 5)));

/** HEX 颜色归一化：非法输入回退白色 */
export const normalizeHex = (v: string | undefined): string => {
  if (!v) return '#FFFFFF';
  const s = v.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(s)) return s.toUpperCase();
  if (/^[0-9a-fA-F]{6}$/.test(s)) return `#${s.toUpperCase()}`;
  return '#FFFFFF';
};

/** 生成灯 id（主灯固定 'main'，补光按序号） */
export const makeLightId = (index: number): string => (index === 0 ? 'main' : `fill-${index}`);

/** 生成灯显示名（主光 / 补光 1 / 补光 2） */
export const makeLightLabel = (index: number): string => (index === 0 ? '主光' : `补光 ${index}`);

/** 单盏灯参数 clamp + 默认值补全 */
export const normalizeLight = (light: Partial<LightSourceParams>, index: number, isMain: boolean): LightSourceParams => ({
  id: light.id || makeLightId(index),
  label: light.label || makeLightLabel(index),
  isMain,
  horizontalAngle: clampHorizontalAngle(light.horizontalAngle ?? -45),
  verticalAngle: clampVerticalAngle(light.verticalAngle ?? 60),
  lightType: light.lightType === 'hard' ? 'hard' : 'soft',
  intensity: clampIntensity(light.intensity ?? 5),
  color: normalizeHex(light.color ?? '#FFFFFF'),
});

/** 打开面板时的参数初始化：存档恢复或默认单盏主灯（方案 §5） */
export function initLightingParams(saved?: LightingParams): LightingParams {
  const rawLights = Array.isArray(saved?.lights) ? saved!.lights! : [];
  const lights = rawLights
    .slice(0, MAX_LIGHTS)
    .map((l, i) => normalizeLight(l, i, i === 0));
  if (lights.length === 0) lights.push(normalizeLight({ id: 'main', label: '主光' }, 0, true));
  return {
    presetId: saved?.presetId || 'custom',
    sceneRotation: clampHorizontalAngle(saved?.sceneRotation ?? 0),
    lights,
    activeLightId: lights.some((l) => l.id === saved?.activeLightId)
      ? saved!.activeLightId!
      : lights[0].id,
  };
}

/** 默认参数（重置按钮用）：单盏主灯 水平-45°/垂直60°/柔光/强度5/白色 */
export const DEFAULT_LIGHTING_PARAMS: LightingParams = {
  presetId: 'custom',
  sceneRotation: 0,
  lights: [normalizeLight({ id: 'main', label: '主光' }, 0, true)],
  activeLightId: 'main',
};

/** 按预设 id 取预设对象 */
export const findPreset = (id: string): LightingPreset | undefined =>
  LIGHTING_PRESETS.find((p) => p.id === id);
